import * as React from 'react';
import * as ImageService from '../../Service/ImageService';
import CameraOutlined from '@ant-design/icons/CameraOutlined';
import { FormInstance } from 'antd/lib/form';
import { Button, Col, Row, message } from 'antd';
import TreeCameraCapture from '../../Component/TreeCameraCapture';
import { IMAGE_PROCESSING_FAILED } from '../../Helper/BannerMessages';

// Devices whose browser turns `<input type="file" capture>` into a camera: there the photo is staged
// in the browser's own storage before the page sees it, so the live camera is used instead (see
// TreeCameraCapture). Desktops keep the file picker, which is what people expect there, and it is
// also the fallback when the camera cannot be started.
const CAMERA_CAPTURE_DEVICE = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

interface TreeImageUploadState {
  cameraOpen: boolean;
}

interface TreeImageUploadProps {
  name: string;
  formRef: React.RefObject<FormInstance>;
  onClick?: Function;
  formSetState: Function;
  enabled: boolean;
  isPlanterValidator?: boolean;
  onTreeProcessed?: Function;
  /** Accuracy of the position in meters, shown in the camera popup (see TreeCameraCapture). */
  gpsAccuracy?: number | null;
  /** Called once a photo has been shot from the live camera */
  onShot?: () => void;
  /** Called when the camera popup is closed without a photo */
  onCameraDismissed?: () => void;
  /** Screen point the camera popup collapses towards when it closes (the queue item). */
  getCollapsePoint?: () => { x: number; y: number } | null;
  /** Reports that the photo is being compressed and queued, so the caller can show it. */
  onProcessingChange?: (processing: boolean) => void;
}

export default class TreeImageUpload extends React.Component<TreeImageUploadProps, TreeImageUploadState> {
  private previewUrl: string | null = null;
  private inputRef = React.createRef<HTMLInputElement>();
  // The frame waiting for the camera popup to be gone, as the function that encodes it: encoding it
  // while the modal is still collapsing slows the UI animation down, and compressing it before the popup
  // is gone would put the loading indicator above a popup that is on its way out.
  private pendingPhoto: (() => Promise<Blob>) | null = null;

  state: TreeImageUploadState = {
    cameraOpen: false,
  };

  constructor(props: TreeImageUploadProps) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.onFileSelected = this.onFileSelected.bind(this);
    this.onPhotoTaken = this.onPhotoTaken.bind(this);
    this.onCameraClosed = this.onCameraClosed.bind(this);
    this.closeCamera = this.closeCamera.bind(this);
    this.useCameraApp = this.useCameraApp.bind(this);
  }

  componentWillUnmount() {
    this.revokePreviewUrl();
  }

  revokePreviewUrl() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  // Only the planter validator flow queues the tree automatically. It is the one that has to show the
  // queue item working, from the press until the tree really is in the queue (see onPhotoTaken and
  // processPhoto).
  private queuesAutomatically(): boolean {
    return !!(this.props.isPlanterValidator && this.props.onTreeProcessed);
  }

  onClick() {
    const { onClick } = this.props;

    if (onClick && !onClick()) {
      return;
    }

    if (CAMERA_CAPTURE_DEVICE && !!navigator.mediaDevices?.getUserMedia) {
      this.setState({ cameraOpen: true });
      return;
    }

    this.inputRef.current?.click();
  }

  onPhotoTaken(encodePhoto: () => Promise<Blob>) {
    // The frame is in hand with best kept position accuracy on screen right now
    this.props.onShot?.();

    // The loading starts on this press, before the popup closes: the queue item the modal collapses
    // towards is then already showing it working. The frame is encoded and compressed once the popup
    // is gone (see onCameraClosed).
    if (this.queuesAutomatically()) {
      this.props.onProcessingChange?.(true);
    }
    this.pendingPhoto = encodePhoto;
    this.setState({ cameraOpen: false });
  }

  async onCameraClosed() {
    const encodePhoto = this.pendingPhoto;
    this.pendingPhoto = null;

    if (!encodePhoto) {
      return;
    }

    try {
      // The JPEG encode starts here, with the popup closed, and the compression follows it.
      await this.processPhoto(await encodePhoto());
    } catch (error) {
      // The frame could not be encoded, so there is nothing to compress.
      console.error('Image processing error:', error);
      message.error(IMAGE_PROCESSING_FAILED);
    }
  }

  closeCamera() {
    // No photo was taken, so nothing of this camera session is kept
    this.props.onCameraDismissed?.();
    this.setState({ cameraOpen: false });
  }

  useCameraApp() {
    // The live camera is not usable, so the photo has to come from the camera app: that only works
    // from this click, a browser opens the picker from a user gesture only.
    this.props.onCameraDismissed?.();
    this.setState({ cameraOpen: false });
    this.inputRef.current?.click();
  }

  async onFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const file = input.files && input.files[0] ? input.files[0] : null;

    // Emptying the input is what releases the raw capture: the browser keeps the data of a file it
    // handed over for as long as something references it, and only the compressed copy is kept
    // below. Nothing else holds on to the original file.
    input.value = '';

    if (!file) {
      return;
    }

    // A photo picked from the camera app is shot at this moment like a frame
    this.props.onShot?.();

    await this.processPhoto(file);
  }

  async processPhoto(photo: Blob) {
    const { formRef, formSetState, isPlanterValidator, onTreeProcessed, onProcessingChange } = this.props;

    // Compressing the photo and queueing the tree are one chain: the caller is told it runs from here
    // - or from the press that took the frame, when the camera popup handed it over - until the tree
    // really is in the queue, so the queue button can show a spinner in place of its count.
    const willQueueHere = this.queuesAutomatically();
    if (willQueueHere) {
      onProcessingChange?.(true);
    }

    try {
      const compressedFile = await ImageService.compressImage(photo);

      this.revokePreviewUrl();
      this.previewUrl = URL.createObjectURL(compressedFile);

      if (formRef.current) {
        formRef.current.setFieldsValue({ photo: compressedFile });
      }

      formSetState({
        hasImage: true,
        previewImage: this.previewUrl,
        confirmed: true
      });

      // 7. If planter validator mode, automatically process the tree
      if (isPlanterValidator && onTreeProcessed) {
        const formValues = formRef.current?.getFieldsValue();
        if (formValues) {
          // Awaited: the loading has to stay until the tree is in the queue.
          await onTreeProcessed(formValues, compressedFile);
        } else {
          console.error('Planter validator - no form values available');
        }
      }

    } catch (error) {
      console.error("Image processing error:", error);
      message.error(IMAGE_PROCESSING_FAILED);
    } finally {
      if (willQueueHere) {
        onProcessingChange?.(false);
      }
    }
  }

  render() {
    const { name, enabled } = this.props;

    return (
      <div>
        <Row justify='center'>
          <Col>
            {/* The file input is the desktop path and the fallback when the camera cannot be started:
                on a phone the camera stream is used instead, so nothing is written to the device
                (see TreeCameraCapture). */}
            <input
              ref={this.inputRef}
              type="file"
              name={name}
              accept="image/jpeg,image/png,image/heic,image/heif,image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={this.onFileSelected}
            />
            <Button type='primary' disabled={!enabled} onClick={this.onClick} className={'btn-tree-picture-upload'}>
              <CameraOutlined /> Take Photo
            </Button>
            <TreeCameraCapture
              open={this.state.cameraOpen}
              accuracy={this.props.gpsAccuracy}
              getCollapsePoint={this.props.getCollapsePoint}
              onCapture={this.onPhotoTaken}
              onCancel={this.closeCamera}
              onUnavailable={this.useCameraApp}
              onClosed={this.onCameraClosed}
            />
          </Col>
        </Row>
      </div>
    );
  }
}

